<script setup lang="ts">

import { usePvpCardProfileStore } from '../services/gameProfile';
import { usePvpCardGameStore } from '../services/gameService';
import Card from './Card.vue';
import { computed, ref, type UnwrapRef } from 'vue';
import { CaretTop } from '@element-plus/icons-vue'
import DialogCardInfo from './DialogCardInfo.vue';
import { getUpgradeCount } from '../services/tools';
import { useCardApi } from '@/api/pvp-card';
import type { CardBasic } from '@/types/pvp-card';
import { useUserStore } from '@/stores/modules/user';
import { formatNumberWithWan } from '@/utils'
import { type CardInventoryItem } from '@/types/pvp-card';
import SoulCurrency from './SoulCurrency.vue';


const userStore = useUserStore()

const pvpCardGameStore = usePvpCardGameStore()
const pvpCardProfileStore = usePvpCardProfileStore()

const visibleCardData = computed(() => {
  const res = {} as Record<string, CardBasic>
  for (const idString in pvpCardGameStore.cardData) {
    const id = Number(idString)
    if (id % 1000 === 0 || id < 1000) {
      continue
    }
    res[id] = pvpCardGameStore.cardData[idString]
  }
  return res
})

const editableTabs = computed(() => {
  const inventory = pvpCardProfileStore.profile.cardInventory
  return pvpCardProfileStore.profile.cardDecks.map((item, key) => ({
    title: item.name,
    name: key,
    cards: item.cards.map((id: number) => {
      return createCard(visibleCardData.value[id], inventory)
    }) as any[]
  }))
})

const cardInventory = computed(() => {
  const inventory = pvpCardProfileStore.profile.cardInventory
  const list = Object.values(visibleCardData.value).map((item) => createCard(item, inventory)).filter(({ id }) => {
    return !pvpCardProfileStore.profile.cardDecks[pvpCardProfileStore.currentDeck]?.cards.includes(id)
  })
  return {
    ownedList: list.filter((i) => i.level > 0),
    notOwnedList: list.filter((i) => i.level === 0),
  }
})
function createCard(item: CardBasic, inventory: any[]): CardInventoryItem {
  const card = inventory.find((i) => i.id === item.id)
  if (card) {
    const maxCount = getUpgradeCount(card.level)
    return {
      ...item,
      count: (card.count || 0) as number,
      level: (card.level || 0) as number,
      maxCount,
      upgradeable: card?.count >= maxCount,
    }
  } else {
    return {
      ...item,
      count: 0,
      level: 0,
      maxCount: 0,
      upgradeable: false,
    }
  }
}

const dialogCardInfoRef = ref<InstanceType<typeof DialogCardInfo>>()
function handleInfo(item: CardInventoryItem) {
  dialogCardInfoRef.value?.handleOpen(item)
}
async function handleAdd(item: CardInventoryItem) {
  if (item.level) {
    await useCardApi({ cardId: item.id, deckIndex: pvpCardProfileStore.currentDeck, action: 'add' })
    pvpCardProfileStore.init()
  } else {
    ElMessage.warning('无法添加未获得的卡牌')
  }
}


async function handleRemove(item: CardInventoryItem) {
  await useCardApi({ cardId: item.id, deckIndex: pvpCardProfileStore.currentDeck, action: 'remove' })
  pvpCardProfileStore.init()
}


</script>

<template>
  <div class="deck-page">
    <slot name="top"></slot>
    <h2 style="margin-bottom: 5px">对决卡组</h2>
    <div class="user-info">
      <span>
        ✨<span class="nickname" style="font-weight: bold" :style="{ background: userStore.nicknameColor }">{{
          userStore.nickname }}</span>
      </span>
      <div style="font-size: 12px">
        <span>
          <SoulCurrency type="dust" /> {{ formatNumberWithWan(userStore.consumables.duelCoin) }}
        </span>
        <span style="margin-left: 12px">
          <SoulCurrency type="quartz" /> {{ formatNumberWithWan(userStore.consumables.duelDiamond) }}
        </span>
        <span style="margin-left: 12px">
          <SoulCurrency type="jade" /> {{
            formatNumberWithWan(userStore.duelCrystal) }}
        </span>
      </div>
    </div>
    <div class="card-deck border-wrap">
      <el-tabs :model-value="pvpCardProfileStore.currentDeck" type="card"
        @tab-change="pvpCardProfileStore.changeCurrentDeck" stretch>
        <el-tab-pane v-for="tab in editableTabs" :key="tab.name" :label="tab.title" :name="tab.name">
          <div class="card-inventory">
            <div class="card-grid" style="padding: 0 10px;">
              <template v-for="(item) of tab.cards">
                <div v-if="item" :key="item.id" class="card-item"
                  :class="{ upgradeable: item.upgradeable, active: item.level }"
                  :style="{ '--percent': Math.min(1, item.count / item.maxCount) * 100 + '%' }"
                  @click="handleInfo(item)">
                  <div class="card-info">
                    <Card :card="item" />
                    <div class="card-level" :class="[item.grade]" :data-level="'lv' + item.level"></div>
                  </div>
                  <div class="card-count">
                    <div class="progress">
                      {{ item.count }}/{{ item.maxCount }}
                    </div>
                    <el-icon class="upgrade-icon" size="16">
                      <CaretTop />
                    </el-icon>
                  </div>
                </div>
                <div v-else></div>
              </template>
            </div>
            <div class="avg-cost" v-if="tab.cards.length">平均卡牌花费{{(tab.cards.reduce((acc, cur) => acc + cur.cost,
              0) / tab.cards.length).toFixed(1)}}🪙</div>
          </div>

        </el-tab-pane>
      </el-tabs>
    </div>
    <div class="card-inventory border-wrap">
      <div class="card-grid">
        <div v-for="(item) of cardInventory.ownedList" :key="item.id" class="card-item"
          :class="{ upgradeable: item.upgradeable, active: item.level }"
          :style="{ '--percent': Math.min(1, item.count / item.maxCount) * 100 + '%' }" @click="handleInfo(item)">
          <div class="card-info">
            <Card :card="item" />
            <div class="card-level" :class="[item.grade]" :data-level="'lv' + item.level"></div>
          </div>
          <div class="card-count">
            <div class="progress">
              {{ item.count }}/{{ item.maxCount }}
            </div>
            <el-icon class="upgrade-icon" size="16">
              <CaretTop />
            </el-icon>
          </div>
        </div>
        <div v-for="(item) of cardInventory.notOwnedList" :key="item.id" class="card-item"
          :class="{ upgradeable: item.upgradeable, active: item.level }"
          :style="{ '--percent': Math.min(1, item.count / item.maxCount) * 100 + '%' }" @click="handleInfo(item)">
          <div class="card-info">

            <Card :card="item" />
            <div class="card-level" :class="[item.grade]" :data-level="'lv' + item.level"></div>
          </div>
          <div class="card-count">
            <div class="progress">
              {{ item.count }}/{{ item.maxCount }}
            </div>
            <el-icon class="upgrade-icon" size="16">
              <CaretTop />
            </el-icon>
          </div>
        </div>
      </div>
    </div>
    <DialogCardInfo :show-btn="true" ref="dialogCardInfoRef" @add-card="handleAdd" @remove-card="handleRemove" />

  </div>
</template>

<style lang="scss" scoped>
.deck-page {
  height: 100%;
  padding-top: 0;
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;

}

.card-deck {
  min-height: 320px;
}

.card-inventory {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  .card-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    padding: 10px;

    .card-item {
      padding: 4px;
      border-radius: 4px;
      box-shadow: 2px 2px 2px rgba(255, 255, 255, 0.4) inset, -2px -2px 2px rgba(100, 100, 100, 0.4) inset;
      background-image: linear-gradient(to top, #334 0%, #112 20%, #001 100%);
      cursor: pointer;

      &:not(.active) {
        filter: grayscale(1);
      }

      &.upgradeable {

        .card-count {
          --color1: #590;
          --color2: #6c0;
        }

        .upgrade-icon {
          animation: jello-vertical 0.9s both infinite;
        }
      }
    }

    .card-level {
      width: 100%;
      height: 18px;
      position: absolute;
      left: 0;
      bottom: 0;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: currentColor;
        opacity: 0.8;
        border-radius: 0 0 4px 4px;
      }

      &::after {
        font-weight: bold;
        font-size: 10px;
        content: attr(data-level);
        position: absolute;
        top: 2px;
        left: 0;
        width: 100%;
        height: 100%;
        text-align: center;
        text-shadow:
          -1px -1px 0 black,
          1px -1px 0 black,
          -1px 1px 0 black,
          1px 1px 0 black;
      }
    }

    .card-info {
      position: relative;
      // aspect-ratio: 0.8;
    }

    .card-count {
      margin-top: 4px;
      display: flex;
      align-items: center;
      --color1: #05f;
      --color2: #5af;
      color: white;
      font-weight: bold;
      font-size: 10px;
      text-align: center;
      color: var(--color1);

      .progress {
        flex: 1;
        padding: 2px 0;
        border-radius: 4px;
        background-image: linear-gradient(to right, var(--color1) 0%, var(--color2) var(--percent), #111 var(--percent));
        color: white;
        overflow: hidden;
      }
    }
  }

  .avg-cost {
    padding: 4px;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    font-size: 12px;
    text-align: center;

  }
}

.user-info {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px 16px;
}
</style>
